<template>
    <div class="analysis-page">
        <AnalysisHeader
            data-pdf-ignore
            :status-info="statusInfo"
            :selected-contract-idx="selectedContractIdx"
            :valid-contracts="validContractsForPeriod"
            :analysis-type="analysisType"
            :current-year="currentYear"
            :current-month="currentMonth"
            :current-date-display="currentDateDisplay"
            :show-date-picker="showDatePicker"
            :is-loading="loading"
            :can-export="!!analysis && !loading"
            :is-exporting="exporting"
            @contract-select="onContractSelect"
            @analysis-type-change="changeAnalysisType"
            @date-navigate="navigateDate"
            @year-change="changeYear"
            @month-select="selectMonth"
            @date-picker-toggle="showDatePicker = !showDatePicker"
            @date-picker-close="showDatePicker = false"
            @refresh="loadAnalysis(true)"
            @settings-open="$refs.settingsModal.show()"
            @usage-analysis-open="openUsageAnalysisModal"
            @pdf-export="exportPdf"
        />

        <div ref="content" class="analysis-content" :class="{ 'is-loading': loading && analysis }">
            <div v-if="loading && !analysis" class="analysis-state">
                <i class="fas fa-spinner fa-spin"></i>
                <span>데이터를 불러오는 중...</span>
            </div>

            <div v-else-if="contractsLoaded && validContractsForPeriod.length === 0" class="analysis-state">
                <i class="fas fa-file-contract"></i>
                <span>선택한 기간에 유효한 요금 계약이 없습니다</span>
                <small>설정에서 계약을 등록하거나 기간을 변경하세요</small>
            </div>

            <div v-else-if="errorMessage && !analysis" class="analysis-state">
                <i class="fas fa-exclamation-circle"></i>
                <span>{{ errorMessage }}</span>
            </div>

            <template v-else-if="analysis">
                <div v-if="isPending" class="pending-banner">
                    <i class="fas fa-hourglass-half"></i>
                    {{ statusInfo.message || "선택한 기간의 요금 데이터가 아직 없습니다" }}
                </div>

                <KpiStrip :analysis="analysis" :analysis-type="analysisType" :period="currentDate" />

                <div class="ebc-row ebc-row--pattern" :class="{ 'has-heatmap': analysisType === 'monthly' }">
                    <UsagePatternPanel
                        :analysis="analysis"
                        :analysis-type="analysisType"
                        :grid="usageGrid.data"
                        :grid-loading="usageGrid.loading"
                        :grid-error="usageGrid.error"
                    />
                    <UsageHeatmapPanel
                        v-if="analysisType === 'monthly'"
                        :grid="usageGrid.data"
                        :grid-loading="usageGrid.loading"
                        :grid-error="usageGrid.error"
                        :unit="unit"
                    />
                </div>

                <CapacityOptimizerPanel
                    class="ebc-row--capacity"
                    :capacity="analysis.capacity"
                    :analysis-type="analysisType"
                />
            </template>

            <div v-if="loading && analysis" data-pdf-ignore class="loading-chip">
                <i class="fas fa-spinner fa-spin"></i>
                데이터를 불러오는 중...
            </div>
        </div>

        <SettingsModal ref="settingsModal" />

        <UsageAnalysisModal
            v-if="selectedContractIdx"
            :show="showUsageAnalysisModal"
            :contract-idx="selectedContractIdx"
            :current-date="currentDate"
            @update:show="showUsageAnalysisModal = $event"
        />
    </div>
</template>

<script>
import html2pdf from "html2pdf.js";
import backEndApi from "@/api/backEndApi";
import { getContractDisplayText } from "../../utils/helpers.js";
import AnalysisHeader from "./AnalysisHeader.vue";
import KpiStrip from "./KpiStrip.vue";
import CapacityOptimizerPanel from "./CapacityOptimizerPanel.vue";
import UsagePatternPanel from "./UsagePatternPanel.vue";
import UsageHeatmapPanel from "./UsageHeatmapPanel.vue";
import SettingsModal from "../modals/SettingsModal.vue";
import UsageAnalysisModal from "../modals/UsageAnalysisModal.vue";

export default {
    name: "AnalysisPage",
    components: {
        AnalysisHeader,
        KpiStrip,
        CapacityOptimizerPanel,
        UsagePatternPanel,
        UsageHeatmapPanel,
        SettingsModal,
        UsageAnalysisModal,
    },
    data() {
        const now = new Date();
        return {
            analysisType: "monthly",
            currentYear: now.getFullYear(),
            currentMonth: now.getMonth() + 1,
            showDatePicker: false,

            contracts: [],
            contractsLoaded: false,
            selectedContractIdx: null,

            analysis: null,
            loading: false,
            errorMessage: "",
            requestSeq: 0,

            usageGrid: { key: null, data: null, loading: false, error: "" },

            showUsageAnalysisModal: false,
            exporting: false,
        };
    },
    computed: {
        currentDate() {
            return this.analysisType === "monthly"
                ? `${this.currentYear}${String(this.currentMonth).padStart(2, "0")}`
                : `${this.currentYear}`;
        },
        currentDateDisplay() {
            return this.analysisType === "monthly"
                ? `${this.currentYear}년 ${this.currentMonth}월`
                : `${this.currentYear}년`;
        },
        /** 조회 기간과 계약 유효기간이 겹치는 활성 계약 */
        validContractsForPeriod() {
            const year = this.currentYear;
            const monthly = this.analysisType === "monthly";
            const firstMonth = monthly ? this.currentMonth : 1;
            const lastMonth = monthly ? this.currentMonth : 12;
            const lastDay = new Date(year, lastMonth, 0).getDate();
            const periodStart = `${year}-${String(firstMonth).padStart(2, "0")}-01`;
            const periodEnd = `${year}-${String(lastMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
            return this.contracts.filter(
                (contract) =>
                    contract.enabled === "Y" &&
                    (!contract.startDate || contract.startDate <= periodEnd) &&
                    (!contract.endDate || contract.endDate >= periodStart),
            );
        },
        statusInfo() {
            return this.analysis?.status || null;
        },
        isPending() {
            return this.statusInfo?.status === "pending";
        },
        energySource() {
            return this.analysis?.contractInfo?.energySourceCode || null;
        },
        unit() {
            return this.analysis?.contractInfo?.rawUnit || "kWh";
        },
    },
    async mounted() {
        await this.loadContracts();
        this.ensureSelectedContract();
        this.loadAnalysis();
    },
    methods: {
        async loadContracts() {
            try {
                const response = await backEndApi.energyBillCalc.getContractList();
                this.contracts = Array.isArray(response?.data) ? response.data : [];
            } catch (error) {
                console.error("Failed to load contracts:", error);
                this.contracts = [];
            } finally {
                this.contractsLoaded = true;
            }
        },
        /** 기간 변경으로 선택 계약이 유효하지 않으면 첫 유효 계약으로 바꾼다 */
        ensureSelectedContract() {
            const valid = this.validContractsForPeriod;
            if (!valid.some((contract) => contract.contractIdx === this.selectedContractIdx)) {
                this.selectedContractIdx = valid.length > 0 ? valid[0].contractIdx : null;
            }
        },
        async loadAnalysis(forcedRefresh = false) {
            if (!this.selectedContractIdx) {
                this.analysis = null;
                return;
            }
            const seq = ++this.requestSeq;
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await backEndApi.energyBillCalc.getScreenAnalysis({
                    contractIdx: this.selectedContractIdx,
                    type: this.analysisType,
                    date: this.currentDate,
                    forcedRefresh: forcedRefresh ? 1 : undefined,
                });
                if (seq !== this.requestSeq) return;
                this.analysis = response?.data || null;
                this.loadUsageGrid(forcedRefresh);
            } catch (error) {
                if (seq !== this.requestSeq) return;
                console.error("Failed to load analysis:", error);
                this.analysis = null;
                this.errorMessage = "요금 분석 데이터를 불러오지 못했습니다.";
                this.$bvToast.toast(this.errorMessage, { title: "오류", variant: "danger", solid: true });
            } finally {
                if (seq === this.requestSeq) this.loading = false;
            }
        },
        async loadUsageGrid(force = false) {
            const contractIdx = this.selectedContractIdx;
            const regYm = this.currentDate;
            if (this.analysisType !== "monthly" || !contractIdx) {
                this.usageGrid = { key: null, data: null, loading: false, error: "" };
                return;
            }
            const key = `${contractIdx}_${regYm}`;
            if (!force && this.usageGrid.key === key && (this.usageGrid.data || this.usageGrid.loading)) return;
            this.usageGrid = { key, data: null, loading: true, error: "" };
            let payload = null;
            let failed = false;
            try {
                const response = await backEndApi.energyBillCalc.getScreenUsagePattern({ contractIdx, regYm });
                payload = response?.data;
            } catch (error) {
                console.error("Failed to load usage pattern:", error);
                failed = true;
            }
            // 응답 사이에 계약·기간이 바뀌었으면 버린다
            if (this.usageGrid.key !== key) return;
            this.usageGrid = {
                key,
                data: !failed && payload && Array.isArray(payload.grid) && payload.grid.length ? payload : null,
                loading: false,
                error: failed ? "시간대별 사용량을 불러오지 못했습니다" : "",
            };
        },
        onPeriodChanged() {
            this.ensureSelectedContract();
            this.loadAnalysis();
        },
        onContractSelect(contractIdx) {
            if (!contractIdx || contractIdx === this.selectedContractIdx) return;
            this.selectedContractIdx = contractIdx;
            this.loadAnalysis();
        },
        changeAnalysisType(type) {
            if (type === this.analysisType) return;
            this.analysisType = type;
            this.onPeriodChanged();
        },
        navigateDate(direction) {
            if (this.analysisType === "monthly") {
                const cursor = new Date(this.currentYear, this.currentMonth - 1 + direction, 1);
                this.currentYear = cursor.getFullYear();
                this.currentMonth = cursor.getMonth() + 1;
            } else {
                this.currentYear += direction;
            }
            this.onPeriodChanged();
        },
        changeYear(direction) {
            this.currentYear += direction;
            this.onPeriodChanged();
        },
        selectMonth(month) {
            this.currentMonth = month;
            this.showDatePicker = false;
            this.onPeriodChanged();
        },
        openUsageAnalysisModal() {
            if (this.analysisType === "yearly") {
                this.$bvToast.toast("월간 기간에서만 사용 가능합니다.", {
                    title: "알림",
                    variant: "info",
                    solid: true,
                });
                return;
            }
            if (!this.selectedContractIdx) return;
            this.showUsageAnalysisModal = true;
        },
        /** 현재 화면을 화면 크기 그대로 1페이지 PDF 로 저장한다 */
        async exportPdf() {
            if (!this.analysis || this.loading || this.exporting) return;
            this.exporting = true;
            this.showDatePicker = false;
            await this.$nextTick();

            const element = this.$el;
            const width = Math.ceil(element.getBoundingClientRect().width);
            const titleHeight = 36;
            // 헤더 대신 제목 줄을 넣으므로 내용 영역 높이를 그대로 유지해 패널 크기가 화면과 같게 한다
            const height = Math.ceil(this.$refs.content.getBoundingClientRect().height) + titleHeight;
            const background = this.resolveBackground(element);
            const title = this.buildPdfTitle(titleHeight);

            const margin = 16;
            // 단위 pt 로 지정해야 html2pdf 의 복제 컨테이너 폭(px)이 화면 폭과 일치한다
            const pt = (px) => (px * 72) / 96;
            try {
                await html2pdf()
                    .set({
                        margin: pt(margin),
                        filename: this.pdfFileName(),
                        image: { type: "jpeg", quality: 0.95 },
                        html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 },
                        jsPDF: {
                            unit: "pt",
                            // 반올림으로 2페이지째가 생기지 않게 높이에 2px 여유를 둔다
                            format: [pt(width + margin * 2), pt(height + margin * 2 + 2)],
                            orientation: width >= height ? "landscape" : "portrait",
                            compress: true,
                        },
                    })
                    .from(element)
                    .toContainer()
                    .then(function () {
                        // 화면은 건드리지 않고 html2pdf 복제본만 고친다 (높이가 부모 기준이라 px 로 고정)
                        const page = this.prop.container.firstElementChild;
                        page.querySelectorAll("[data-pdf-ignore]").forEach((node) => node.remove());
                        page.insertBefore(title, page.firstChild);
                        Object.assign(page.style, { width: `${width}px`, height: `${height}px`, background });
                    })
                    .save();
            } catch (error) {
                console.error("Failed to export PDF:", error);
                this.$bvToast.toast("PDF 를 만들지 못했습니다.", { title: "오류", variant: "danger", solid: true });
            } finally {
                this.exporting = false;
            }
        },
        buildPdfTitle(height) {
            const title = document.createElement("div");
            Object.assign(title.style, {
                display: "flex",
                alignItems: "center",
                gap: "10px",
                height: `${height}px`,
                flexShrink: "0",
                padding: "0 4px",
                color: "var(--ebc-text)",
                fontSize: "15px",
                fontWeight: "700",
                whiteSpace: "nowrap",
            });
            const name = document.createElement("span");
            name.textContent = this.contractName() || "계약";
            const period = document.createElement("span");
            Object.assign(period.style, { color: "var(--ebc-text-sub)", fontSize: "13px", fontWeight: "600" });
            period.textContent = this.currentDateDisplay;
            title.append(name, period);
            return title;
        },
        contractName() {
            const contract = this.contracts.find((item) => item.contractIdx === this.selectedContractIdx);
            return contract?.contractName || getContractDisplayText(contract);
        },
        resolveBackground(element) {
            for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
                const color = getComputedStyle(node).backgroundColor;
                if (color && color !== "transparent" && !/rgba\(.*,\s*0\)$/.test(color)) return color;
            }
            return "#ffffff";
        },
        pdfFileName() {
            const contractName = (this.contractName() || "계약").replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "");
            const period =
                this.analysisType === "monthly"
                    ? `${this.currentYear}-${String(this.currentMonth).padStart(2, "0")}`
                    : `${this.currentYear}`;
            return `요금시뮬레이션_${contractName}_${period}.pdf`;
        },
    },
};
</script>

<style scoped>
.analysis-page {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: var(--ebc-text);
    --ebc-text-muted: var(--ebc-gray-3);
    --ebc-positive: #3f9b78;
    --ebc-danger: #c25b5b;
    --ebc-primary: var(--ebc-navy-1);
    --ebc-primary-hover: #0c162b;
    --ebc-primary-soft: var(--ebc-navy-3);
    --ebc-primary-tint: rgba(47, 111, 174, 0.08);
    --ebc-primary-tint-strong: rgba(47, 111, 174, 0.16);
    --ebc-shadow-panel: 0 1px 2px rgba(19, 32, 58, 0.06), 0 3px 10px rgba(19, 32, 58, 0.08);
}

.analysis-content {
    --ebc-gap: 12px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    /* 스크롤 영역이 패널 그림자를 자르지 않도록 여백을 둔다 */
    padding: 0 4px 8px;
    overflow-y: auto;
    position: relative;
}

.analysis-content.is-loading > :not(.loading-chip) {
    opacity: 0.55;
    pointer-events: none;
    transition: opacity 0.2s var(--ebc-ease);
}

.ebc-row {
    display: grid;
    gap: var(--ebc-gap);
}

.ebc-row--pattern {
    flex: 1 1 0;
    min-height: 0;
}

.ebc-row--pattern.has-heatmap {
    grid-template-columns: minmax(0, 1fr) 400px;
}

.ebc-row--capacity {
    flex: 1.15 1 0;
    min-height: 0;
}

.analysis-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    color: var(--ebc-text-sub);
    font-size: 14px;
    font-weight: 600;
}

.analysis-state i {
    font-size: 28px;
    color: var(--ebc-primary-soft);
}

.analysis-state small {
    font-size: 12px;
    font-weight: 500;
    color: var(--ebc-text-muted);
}

.pending-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: var(--ebc-radius-sm);
    background: var(--ebc-warn-tint);
    color: var(--ebc-warn);
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
}

.loading-chip {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: var(--ebc-radius-pill);
    background: rgba(15, 23, 42, 0.78);
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    z-index: 5;
    flex-shrink: 0;
}
</style>
